/* ==========================================================================
   PUREGANIC — Build Your Own Bundle (BYOB)
   Vanilla, scoped per section. No libraries.

   Front-end only for now: build a bundle, running totals, tier-progress, and a
   functional add-to-cart via /cart/add.js. The Bundler discount hookup is STUBBED
   and marked `PUREGANIC-BUNDLER-TODO` — wired once the brand dev provides details.
   ========================================================================== */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '${{amount}}';
    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var value = '';
    var match = fmt.match(placeholder);
    switch (match ? match[1] : 'amount') {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  function init(root) {
    if (!root || root.dataset.pgbReady === 'true') return;
    root.dataset.pgbReady = 'true';

    var moneyFormat = root.dataset.moneyFormat;
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-pgb-card]'));
    var linesEl = root.querySelector('[data-pgb-lines]');
    var emptyEl = root.querySelector('[data-pgb-empty]');
    var subtotalEl = root.querySelector('[data-pgb-subtotal]');
    var savingRow = root.querySelector('[data-pgb-saving-row]');
    var savingEl = root.querySelector('[data-pgb-saving]');
    var savingLabel = root.querySelector('[data-pgb-saving-label]');
    var totalEl = root.querySelector('[data-pgb-total]');
    var progressLabel = root.querySelector('[data-pgb-progress-label]');
    var progressFill = root.querySelector('[data-pgb-progress-fill]');
    var mobilebar = root.querySelector('[data-pgb-mobilebar]');
    var mbCount = root.querySelector('[data-pgb-mobilebar-count]');
    var mbTotal = root.querySelector('[data-pgb-mobilebar-total]');
    var ctaButtons = Array.prototype.slice.call(root.querySelectorAll('[data-pgb-add-bundle]'));
    var mbHint = root.querySelector('[data-pgb-mobilebar-hint]');
    var sheetToggle = root.querySelector('[data-pgb-sheet-toggle]');
    var sheetClose = root.querySelector('[data-pgb-sheet-close]');
    var backdrop = root.querySelector('[data-pgb-backdrop]');
    var deals = Array.prototype.slice.call(root.querySelectorAll('[data-deal-tier]'));
    var currentIndicator = root.querySelector('[data-pgb-current]');
    var countEl = root.querySelector('[data-pgb-count]');
    var summaryToggle = root.querySelector('[data-pgb-summary-toggle]');
    var linesWrap = root.querySelector('[data-pgb-lines-wrap]');

    // Drawer mode (product page): the trigger card opens the full builder in a drawer.
    // IMPORTANT: we PORTAL the drawer to <body> on first open. Impact's product page has a
    // transformed ancestor (scroll animations), which makes `position: fixed` resolve against
    // that ancestor instead of the viewport — clipping the drawer's top (header + first cards)
    // off-screen. Moving the node to <body> escapes the transform so `fixed` means the viewport.
    // The builder's buttons keep working: their listeners are bound to element references below,
    // and moving a node in the DOM preserves its listeners. Open-state lives on the drawer node
    // itself (not root) since the drawer is no longer a descendant of root after the portal.
    var pgbDrawer = root.querySelector('[data-pgb-drawer]');
    if (pgbDrawer) {
      var portaled = false;
      var openDrawer = function () {
        if (!portaled) { document.body.appendChild(pgbDrawer); portaled = true; }
        pgbDrawer.hidden = false;
        var body = pgbDrawer.querySelector('.pureganic-byob__drawer-body');
        if (body) body.scrollTop = 0;
        requestAnimationFrame(function () { pgbDrawer.classList.add('is-open'); });
        document.documentElement.classList.add('pgb-drawer-open');
      };
      var closeDrawer = function () {
        pgbDrawer.classList.remove('is-open');
        document.documentElement.classList.remove('pgb-drawer-open');
        setTimeout(function () { if (!pgbDrawer.classList.contains('is-open')) pgbDrawer.hidden = true; }, 350);
      };
      Array.prototype.forEach.call(root.querySelectorAll('[data-pgb-open]'), function (b) { b.addEventListener('click', openDrawer); });
      // Close controls live inside the drawer node — bind from pgbDrawer so they survive the portal.
      Array.prototype.forEach.call(pgbDrawer.querySelectorAll('[data-pgb-drawer-close]'), function (b) { b.addEventListener('click', closeDrawer); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && pgbDrawer.classList.contains('is-open')) closeDrawer(); });
    }

    // Product-page bundle (mobile): reveal + dock the summary ONLY once the shopper has
    // scrolled to the end of the product cards (anchor sits right after the grid). So the
    // summary never hovers over the cards on first glance — you see the products first.
    // The summary (mobile) reveals when the bundle has ≥1 item — see render(). It stays hidden
    // while empty, so the shopper browses products first and the summary appears (as feedback)
    // the moment they add one.

    // Tiers, built from data attributes (min items -> discount %). Items needed per tier are
    // configurable on the BYOB page (data-tierN-min); the product page falls back to 2/3/4.
    var tierCount = parseInt(root.dataset.tierCount, 10) || 4;
    var tiers = [
      { min: parseInt(root.dataset.tier2Min, 10) || 2, disc: parseFloat(root.dataset.tier2) || 0 },
      { min: parseInt(root.dataset.tier3Min, 10) || 3, disc: parseFloat(root.dataset.tier3) || 0 },
      { min: parseInt(root.dataset.tier4Min, 10) || 4, disc: parseFloat(root.dataset.tier4) || 0 }
    ].slice(0, tierCount - 1)
      .filter(function (t) { return t.disc > 0; })
      .sort(function (a, b) { return a.min - b.min; });

    // Minimum items before the bundle CTA is enabled. Defaults to the first tier (BYOB),
    // but the product page passes data-min-items="1" (the current product is pre-added).
    var minItems = parseInt(root.dataset.minItems, 10) || (tiers.length ? tiers[0].min : 2);
    var maxTier = tiers.length ? tiers[tiers.length - 1] : null;

    // State: variantId -> { qty, price, title, image }
    var state = {};

    // On the product page the current product starts in the bundle.
    if (root.dataset.preaddVariant) {
      state[root.dataset.preaddVariant] = {
        qty: 1,
        price: parseInt(root.dataset.preaddPrice, 10) || 0,
        title: root.dataset.preaddTitle || '',
        image: root.dataset.preaddImage || ''
      };
    }

    function totalCount() {
      return Object.keys(state).reduce(function (n, v) { return n + state[v].qty; }, 0);
    }
    function subtotalCents() {
      return Object.keys(state).reduce(function (n, v) { return n + state[v].qty * state[v].price; }, 0);
    }
    function currentDiscount(count) {
      var disc = 0;
      tiers.forEach(function (t) { if (count >= t.min) disc = t.disc; });
      return disc;
    }
    function nextTier(count) {
      for (var i = 0; i < tiers.length; i++) { if (count < tiers[i].min) return tiers[i]; }
      return null;
    }

    function setQty(variant, qty, meta) {
      if (qty <= 0) {
        delete state[variant];
      } else if (state[variant]) {
        state[variant].qty = qty;
      } else if (meta) {
        state[variant] = { qty: qty, price: meta.price, title: meta.title, image: meta.image };
      }
      render();
    }

    function renderCards() {
      cards.forEach(function (card) {
        var variant = card.dataset.variant;
        var qty = state[variant] ? state[variant].qty : 0;
        var addBtn = card.querySelector('[data-pgb-add]');
        var stepper = card.querySelector('[data-pgb-stepper]');
        if (!addBtn || !stepper) return;
        if (qty > 0) {
          addBtn.hidden = true;
          stepper.hidden = false;
          card.querySelector('[data-pgb-qty]').textContent = qty;
          card.classList.add('is-in-bundle');
        } else {
          addBtn.hidden = false;
          stepper.hidden = true;
          card.classList.remove('is-in-bundle');
        }
      });
    }

    function renderLines() {
      linesEl.textContent = '';
      var keys = Object.keys(state);
      emptyEl.hidden = keys.length > 0;
      keys.forEach(function (variant) {
        var item = state[variant];
        var li = document.createElement('li');
        li.className = 'pureganic-byob__line';
        li.innerHTML =
          '<span class="pureganic-byob__line-media">' +
            (item.image ? '<img src="' + item.image + '" alt="" loading="lazy">' : '') +
          '</span>' +
          '<span class="pureganic-byob__line-info">' +
            '<span class="pureganic-byob__line-name"></span>' +
            '<span class="pureganic-byob__line-controls">' +
              '<button type="button" class="pureganic-byob__step" data-line-dec aria-label="Remove one">−</button>' +
              '<span class="pureganic-byob__qty">' + item.qty + '</span>' +
              '<button type="button" class="pureganic-byob__step" data-line-inc aria-label="Add one">+</button>' +
            '</span>' +
          '</span>' +
          '<span class="pureganic-byob__line-price">' + formatMoney(item.qty * item.price, moneyFormat) + '</span>';
        li.querySelector('.pureganic-byob__line-name').textContent = item.title;
        li.querySelector('[data-line-dec]').addEventListener('click', function () { setQty(variant, item.qty - 1); });
        li.querySelector('[data-line-inc]').addEventListener('click', function () { setQty(variant, item.qty + 1); });
        linesEl.appendChild(li);
      });
    }

    // Milestone bar: fill to the marker of the pack size reached, mark each milestone,
    // and show the "add N more" nudge.
    function renderProgress(count) {
      var reached = Math.min(count, tierCount);
      // Fill (as % of the bar) so its right edge lands on the reached dot; the bar and
      // dots are inset by half a column, so the per-step width is 1/tierCount.
      var pct = tierCount > 0 ? Math.max(0, ((reached - 1) / tierCount) * 100) : 0;
      if (progressFill) progressFill.style.inlineSize = pct + '%';

      // Current = the highest marker the count has reached (markers may not be consecutive).
      var current = 0;
      deals.forEach(function (d) {
        var t = parseInt(d.dataset.dealTier, 10);
        if (count >= t && t > current) current = t;
      });
      deals.forEach(function (d) {
        var t = parseInt(d.dataset.dealTier, 10);
        d.classList.toggle('is-reached', count >= t);
        d.classList.toggle('is-current', count >= 1 && t === current);
      });

      var next = nextTier(count);
      if (progressLabel) {
        if (count === 0) {
          progressLabel.textContent = tiers.length ? ('Add ' + tiers[0].min + ' to save ' + tiers[0].disc + '%') : '';
        } else if (next) {
          progressLabel.textContent = 'Add ' + (next.min - count) + ' more to save ' + next.disc + '%';
        } else if (maxTier) {
          progressLabel.textContent = "You've unlocked " + maxTier.disc + '% off — best value';
        }
      }
    }

    function render() {
      var count = totalCount();
      var subtotal = subtotalCents();
      var disc = currentDiscount(count);
      var saving = Math.round(subtotal * disc / 100);

      // Reveal + dock the summary (mobile) only once there's something in the bundle; tuck it
      // away again when emptied. Keeps the browsing view clean and shows the summary as feedback
      // on the first "Add to Bundle".
      root.classList.toggle('is-summary-revealed', count > 0);

      renderCards();
      renderLines();
      renderProgress(count);

      // "This product is in your bundle" reassurance — only while the current product is in.
      if (currentIndicator) {
        var pv = root.dataset.preaddVariant;
        currentIndicator.hidden = !(pv && state[pv]);
      }

      if (countEl) countEl.textContent = count > 0 ? (count + (count === 1 ? ' item' : ' items')) : 'Your bundle';

      subtotalEl.textContent = formatMoney(subtotal, moneyFormat);
      if (saving > 0) {
        savingRow.hidden = false;
        savingLabel.textContent = 'Bundle saving (' + disc + '%)';
        savingEl.textContent = '−' + formatMoney(saving, moneyFormat);
      } else {
        savingRow.hidden = true;
      }
      totalEl.textContent = formatMoney(subtotal - saving, moneyFormat);

      var ready = count >= minItems;
      var ctaLabel;
      if (!ready) {
        ctaLabel = 'Add at least ' + minItems + (minItems === 1 ? ' item' : ' items');
      } else if (count === 1) {
        // A single item isn't a bundle — plain add to cart (product page, pre-added product).
        ctaLabel = 'Add to cart';
      } else if (saving > 0) {
        ctaLabel = 'Add bundle to cart · Save ' + formatMoney(saving, moneyFormat);
      } else {
        ctaLabel = 'Add bundle to cart';
      }
      ctaButtons.forEach(function (btn) {
        btn.disabled = !ready;
        btn.textContent = ctaLabel;
      });

      if (mobilebar) {
        // Bar stays visible on mobile (persistent affordance); it just reflects state.
        if (count === 0) closeSheet();
        if (mbCount) mbCount.textContent = count + (count === 1 ? ' item' : ' items');
        if (mbTotal) mbTotal.textContent = formatMoney(subtotal - saving, moneyFormat);
        if (mbHint) mbHint.textContent = shortHint(count);
      }
    }

    function shortHint(count) {
      var next = nextTier(count);
      if (next) return 'Add ' + (next.min - count) + ' more for ' + next.disc + '%';
      if (maxTier && count >= maxTier.min) return maxTier.disc + '% off unlocked';
      return '';
    }

    function openSheet() {
      root.classList.add('is-sheet-open');
      if (backdrop) backdrop.hidden = false;
      if (sheetToggle) sheetToggle.setAttribute('aria-expanded', 'true');
    }
    function closeSheet() {
      root.classList.remove('is-sheet-open');
      if (backdrop) backdrop.hidden = true;
      if (sheetToggle) sheetToggle.setAttribute('aria-expanded', 'false');
    }

    function addBundle() {
      var items = Object.keys(state).map(function (v) { return { id: Number(v), quantity: state[v].qty }; });
      if (!items.length) return;
      ctaButtons.forEach(function (b) { b.disabled = true; });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          /* PUREGANIC-BUNDLER-TODO --------------------------------------------
             Items are in the cart at full price. Wire the Bundler mix-and-match
             automatic discount here once the brand dev confirms the integration
             (same stub as assets/pureganic-bundles.js). ---------------------- */
          window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
            ? window.Shopify.routes.root + 'cart' : '/cart';
        })
        .catch(function () { render(); });
    }

    // --- Wire the grid -------------------------------------------------------
    cards.forEach(function (card) {
      var variant = card.dataset.variant;
      var meta = {
        price: parseInt(card.dataset.price, 10) || 0,
        title: card.dataset.title,
        image: card.dataset.image
      };
      var addBtn = card.querySelector('[data-pgb-add]');
      var inc = card.querySelector('[data-pgb-inc]');
      var dec = card.querySelector('[data-pgb-dec]');
      if (addBtn) addBtn.addEventListener('click', function () { setQty(variant, 1, meta); });
      if (inc) inc.addEventListener('click', function () { setQty(variant, (state[variant] ? state[variant].qty : 0) + 1, meta); });
      if (dec) dec.addEventListener('click', function () { setQty(variant, (state[variant] ? state[variant].qty : 0) - 1, meta); });
    });

    ctaButtons.forEach(function (btn) { btn.addEventListener('click', addBundle); });

    // Collapsible summary: chevron expands/collapses the list of added products.
    if (summaryToggle && linesWrap) {
      summaryToggle.addEventListener('click', function () {
        var open = root.classList.toggle('is-summary-open');
        linesWrap.hidden = !open;
        summaryToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    if (sheetToggle) sheetToggle.addEventListener('click', function () {
      if (root.classList.contains('is-sheet-open')) closeSheet(); else openSheet();
    });
    if (sheetClose) sheetClose.addEventListener('click', closeSheet);
    if (backdrop) backdrop.addEventListener('click', closeSheet);

    render();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-pureganic-byob]').forEach(init);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
